import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Check, X, Sparkles, ShieldCheck, ArrowRight, Zap, Trophy, Ticket, Loader2, Gift } from "lucide-react";
import { useTeams } from "../lib/teams";
import { usePlan } from "../lib/usePlan";
import api from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: false });

const ACCENTS = {
  free: { ring: "ring-slate-200", grad: "from-slate-50 via-white to-slate-100", icon: ShieldCheck, btn: "bg-slate-900 hover:bg-slate-800" },
  pro: { ring: "ring-sky-300", grad: "from-sky-50 via-white to-sky-100", icon: Sparkles, btn: "bg-sky-500 hover:bg-sky-600" },
  club: { ring: "ring-amber-300", grad: "from-amber-50 via-white to-amber-100", icon: Trophy, btn: "bg-amber-500 hover:bg-amber-600" },
};

const fmtEur = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isInteger(n)) return `€${n}`;
  return `€${n.toFixed(2).replace(".", ",")}`;
};

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const { activeTeam } = useTeams();
  const plan = usePlan();
  const navigate = useNavigate();
  const [working, setWorking] = useState(null);
  const [coupon, setCoupon] = useState({ input: "", code: null, info: null, error: null, validating: false });
  const [referral, setReferral] = useState({ code: null, referrerName: null });
  const [searchParams] = useSearchParams();

  // Auto-apply ?ref=CODE from URL
  useEffect(() => {
    const ref = (searchParams.get("ref") || "").trim().toUpperCase();
    if (!ref || !activeTeam) return;
    publicApi
      .get(`/referrals/validate`, { params: { code: ref, team_id: activeTeam.id } })
      .then(({ data }) => {
        setReferral({ code: data.code, referrerName: data.referrer_team_name });
        toast.success(`Codice referral di ${data.referrer_team_name} applicato 🎁`);
      })
      .catch(() => {});
  }, [searchParams, activeTeam]);

  useEffect(() => {
    publicApi.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans);
      setTrialDays(data.trial_days);
    });
  }, []);

  const currentPlan = plan?.plan || "free";
  const planTier = { free: 0, pro: 1, club: 2 };

  const applyCoupon = async () => {
    const code = coupon.input.trim();
    if (!code) return;
    // Validate against Pro by default; backend re-validates per plan at checkout
    setCoupon((c) => ({ ...c, validating: true, error: null, info: null }));
    try {
      const { data } = await publicApi.get(`/coupons/validate`, { params: { code, plan: "pro" } });
      setCoupon({
        input: code, code: data.code, info: data, error: null, validating: false,
      });
      toast.success(`Codice applicato: -${data.savings.toFixed(2)} €`);
    } catch (e) {
      setCoupon((c) => ({
        ...c,
        validating: false,
        info: null,
        code: null,
        error: e?.response?.data?.detail || "Codice non valido",
      }));
    }
  };

  const removeCoupon = () => setCoupon({ input: "", code: null, info: null, error: null, validating: false });

  const handleSubscribe = async (planId) => {
    if (!activeTeam) return toast.error("Seleziona prima una squadra");
    if (planId === "free") {
      navigate("/app/billing");
      return;
    }
    setWorking(planId);
    try {
      const origin = window.location.origin;
      const body = {
        team_id: activeTeam.id,
        plan: planId,
        origin_url: `${origin}/billing/result`,
      };
      if (coupon.code) body.coupon_code = coupon.code;
      else if (referral.code) body.referral_code = referral.code;
      const { data } = await api.post("/billing/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio pagamento");
      setWorking(null);
    }
  };

  const handleStartTrial = async () => {
    if (!activeTeam) return toast.error("Seleziona prima una squadra");
    setWorking("trial");
    try {
      await api.post("/billing/start-trial", { team_id: activeTeam.id });
      toast.success(`Trial di ${trialDays} giorni avviato`);
      await plan.reload();
      navigate("/app/billing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio trial");
    } finally {
      setWorking(null);
    }
  };

  const computeDiscountedPrice = (planId) => {
    if (planId === "free") return null;
    const original = plans.find((p) => p.id === planId)?.price_eur;
    if (!original) return null;
    if (referral.code) {
      // referral always gives 50% off referee
      return Math.max(0, Math.round(original * 0.5 * 100) / 100);
    }
    if (!coupon.info) return null;
    if (coupon.info.type === "percent") {
      return Math.max(0, Math.round(original * (1 - coupon.info.value / 100) * 100) / 100);
    }
    return Math.max(0, Math.round((original - coupon.info.value) * 100) / 100);
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="pricing-page">
      <div className="text-center pt-4">
        <div className="label-eyebrow inline-flex items-center gap-1.5 justify-center">
          <Zap size={12} /> Scegli il piano giusto per la tua società
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3">
          Più tempo per il calcio,<br />meno per la burocrazia
        </h1>
        <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
          Tre piani semplici, nessun vincolo annuale. Annulli quando vuoi.
          Inizia con il trial gratis di {trialDays} giorni del piano Pro.
        </p>
      </div>

      {/* Referral banner if URL had ?ref=CODE */}
      {referral.code && (
        <Card className="p-4 max-w-xl mx-auto bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border-emerald-300" data-testid="referral-applied">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <Gift size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                Invito da <strong>{referral.referrerName}</strong>
              </div>
              <div className="text-xs text-slate-600">
                Codice <span className="font-mono">{referral.code}</span> — riceverai il <strong>50% di sconto</strong> sul primo mese
              </div>
            </div>
            <Button onClick={() => setReferral({ code: null, referrerName: null })} variant="ghost" size="sm" className="text-slate-500">
              <X size={14} />
            </Button>
          </div>
        </Card>
      )}

      {/* Coupon input */}
      <Card className="p-4 max-w-xl mx-auto bg-gradient-to-r from-amber-50 via-white to-amber-50 border-amber-200" data-testid="coupon-card">
        {!coupon.info ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Ticket size={14} className="text-amber-600" />
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">Hai un codice promozionale?</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={coupon.input}
                onChange={(e) => setCoupon((c) => ({ ...c, input: e.target.value.toUpperCase(), error: null }))}
                placeholder="EARLYBIRD50"
                className="font-mono uppercase"
                data-testid="coupon-input"
                onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
              />
              <Button
                onClick={applyCoupon}
                disabled={coupon.validating || !coupon.input.trim()}
                variant="outline"
                className="bg-white shrink-0"
                data-testid="coupon-apply"
              >
                {coupon.validating ? <Loader2 size={13} className="animate-spin" /> : "Applica"}
              </Button>
            </div>
            {coupon.error && <p className="text-xs text-red-600 mt-2">{coupon.error}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3" data-testid="coupon-applied">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                <Check size={16} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono">{coupon.info.code}</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                    {coupon.info.type === "percent" ? `-${coupon.info.value}%` : `-€${coupon.info.value}`}
                  </span>
                </div>
                {coupon.info.notes && <div className="text-xs text-slate-500 truncate">{coupon.info.notes}</div>}
              </div>
            </div>
            <Button onClick={removeCoupon} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900">
              <X size={14} /> Rimuovi
            </Button>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const accent = ACCENTS[p.id];
          const Icon = accent.icon;
          const isCurrent = currentPlan === p.id;
          const isDowngrade = planTier[p.id] < planTier[currentPlan];
          const isUpgrade = planTier[p.id] > planTier[currentPlan];
          const discounted = computeDiscountedPrice(p.id);
          return (
            <Card
              key={p.id}
              className={`relative p-6 rounded-3xl bg-gradient-to-br ${accent.grad} border-0 ring-1 ${accent.ring} ${p.highlight ? "shadow-xl md:scale-105" : "shadow-sm"}`}
              data-testid={`plan-${p.id}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-sky-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow">
                  Più scelto
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={18} className={p.id === "pro" ? "text-sky-600" : p.id === "club" ? "text-amber-600" : "text-slate-600"} />
                  <span className="text-sm font-semibold uppercase tracking-wider text-slate-700">{p.name}</span>
                </div>
                {isCurrent && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>
                )}
              </div>
              <div className="mt-3">
                <div className="flex items-baseline gap-1">
                  {discounted !== null && discounted !== p.price_eur ? (
                    <>
                      <span className="font-heading text-5xl font-bold text-emerald-700">{fmtEur(discounted)}</span>
                      <span className="text-base text-slate-400 line-through">{fmtEur(p.price_eur)}</span>
                    </>
                  ) : (
                    <span className="font-heading text-5xl font-bold">{fmtEur(p.price_eur)}</span>
                  )}
                  <span className="text-sm text-slate-500">/{p.billing_period}</span>
                </div>
                {p.price_yearly_eur && (
                  <div className="text-xs text-emerald-700 font-medium mt-1">
                    oppure {fmtEur(p.price_yearly_eur)}/anno <span className="text-emerald-600">(-20%)</span>
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
              </div>

              <ul className="mt-5 space-y-2 text-sm">
                {p.feature_list.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={15} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
                {(p.missing || []).map((f, i) => (
                  <li key={`m-${i}`} className="flex items-start gap-2">
                    <X size={15} className="text-slate-400 shrink-0 mt-0.5" />
                    <span className="text-slate-400 line-through">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 space-y-2">
                {p.id === "free" && (
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={isCurrent}
                    onClick={() => navigate("/app/billing")}
                    data-testid="plan-action-free"
                  >
                    {isCurrent ? "Piano attuale" : "Continua con Free"}
                  </Button>
                )}
                {p.id === "pro" && !isCurrent && plan?.status === "free" && (
                  <Button
                    variant="outline"
                    className="w-full bg-white"
                    onClick={handleStartTrial}
                    disabled={working === "trial"}
                    data-testid="plan-trial-btn"
                  >
                    {working === "trial" ? "Avvio..." : `Prova ${trialDays} giorni gratis`}
                  </Button>
                )}
                {p.id !== "free" && (
                  <Button
                    className={`w-full text-white ${accent.btn}`}
                    onClick={() => handleSubscribe(p.id)}
                    disabled={working === p.id || isCurrent || isDowngrade}
                    data-testid={`plan-action-${p.id}`}
                  >
                    {working === p.id
                      ? "Attendere..."
                      : isCurrent
                      ? "Piano attuale"
                      : isDowngrade
                      ? "Downgrade"
                      : isUpgrade
                      ? <>Upgrade <ArrowRight size={14} className="ml-1" /></>
                      : <>Sottoscrivi <ArrowRight size={14} className="ml-1" /></>}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6 sm:p-8 mt-12 bg-slate-900 text-white border-0 rounded-3xl">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <h3 className="font-heading text-xl font-bold mb-2">Nessun vincolo</h3>
            <p className="text-sm text-slate-300">Pagamento mensile. Annulli quando vuoi dal pannello "Il mio abbonamento".</p>
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold mb-2">Stripe sicuro</h3>
            <p className="text-sm text-slate-300">Pagamenti gestiti da Stripe — la stessa piattaforma usata da migliaia di SaaS in Italia. Nessun dato carta tocca i nostri server.</p>
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold mb-2">Domande?</h3>
            <p className="text-sm text-slate-300">Scrivici a <a href="mailto:supporto@calcioclub.it" className="underline">supporto@calcioclub.it</a> e ti rispondiamo entro 24h.</p>
          </div>
        </div>
      </Card>

      <div className="text-center pt-6 pb-12">
        <Link to="/app" className="text-sm text-slate-500 hover:text-slate-800 underline">← Torna alla dashboard</Link>
      </div>
    </div>
  );
}

